import React from 'react';

const AccessibleFormField = ({ 
  label, 
  id, 
  error, 
  helpText, 
  required = false,
  children,
  className = ''
}) => {
  const errorId = error ? `${id}-error` : undefined;
  const helpId = helpText ? `${id}-help` : undefined;
  const describedBy = [errorId, helpId].filter(Boolean).join(' ');

  // Helper function to find and update input element in nested structure
  const applyInputAttributes = (element) => {
    // If this is an input, select, or textarea, apply attributes directly
    if (element.type === 'input' || element.type === 'select' || element.type === 'textarea' ||
        (typeof element.type === 'string' && ['input', 'select', 'textarea'].includes(element.type))) {
      return React.cloneElement(element, {
        id,
        'aria-describedby': describedBy || undefined,
        'aria-invalid': error ? 'true' : 'false',
        'aria-required': required ? 'true' : undefined,
        required: required
      });
    }

    // If this element has children, recursively find the input
    if (element.props && element.props.children) {
      const updatedChildren = React.Children.map(element.props.children, (child) => {
        if (React.isValidElement(child)) {
          // Check if child is an input element
          if (child.type === 'input' || child.type === 'select' || child.type === 'textarea') {
            return React.cloneElement(child, {
              id,
              'aria-describedby': describedBy || undefined,
              'aria-invalid': error ? 'true' : 'false',
              'aria-required': required ? 'true' : undefined,
              required: required
            });
          }
          // Recursively search nested children
          return applyInputAttributes(child);
        }
        return child;
      });

      return React.cloneElement(element, {
        id: element.props.id || id,
        'aria-describedby': element.props['aria-describedby'] || (describedBy || undefined),
        children: updatedChildren
      });
    }

    // If no input found, just clone with id (for wrapper divs)
    return React.cloneElement(element, {
      id: element.props.id || id,
      'aria-describedby': element.props['aria-describedby'] || (describedBy || undefined)
    });
  };

  return (
    <div className={`form-field ${className}`}>
      <label 
        htmlFor={id} 
        className="form-label"
      >
        {label}
        {required && (
          <span className="text-red-500 mr-1" aria-label="שדה חובה">
            *
          </span>
        )}
      </label>
      
      {helpText && (
        <div id={helpId} className="text-sm text-gray-600 mb-2">
          {helpText}
        </div>
      )}

      {applyInputAttributes(children)}

      {error && (
        <div 
          id={errorId} 
          className="form-error" 
          role="alert"
          aria-live="polite"
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default AccessibleFormField;
